import * as React from 'react'

import {
  useEngagementCoordinationStore,
  useEngagementCoordinationStoreApi,
} from '../../../context/EngagementCoordinationContext'
import { buildCoordinationResetKeys } from '../../../hooks/engagement/buildCoordinationResetKeys'
import {
  deriveAllResolved,
  selectLocallyResolved,
  selectLocallyUnresolved,
} from '../../../hooks/engagement/engagementCoordinationStore'
import type { CoordinationItem } from '../../../hooks/engagement/engagementCoordinationStore'
import { useEngagementListCoordination } from '../../../hooks/engagement/useEngagementListCoordination'
import { useCommunityConfig } from '../../../CommunityConfig'
import type { MentionsCapabilityChannel } from '../../../CommunityConfig'
import type { BaseCommunityFilters } from '../../../CommunityFilters/types'
import type { MentionData } from '../../context'

type UseMentionListCoordinationParams = {
  mentions: MentionData[]
  channelById: Map<string, MentionsCapabilityChannel>
  filters?: BaseCommunityFilters
  /** Whether the mentions query is still fetching; see the driver's `isLoading`. */
  isLoading?: boolean
}

type UseMentionListCoordinationReturn = {
  /** Manually expand a card, or collapse with `null`. */
  expand: (mentionId: string | null) => void
  /** Expand the next card (immediate; no scroll — see comments adapter). */
  expandNext: (fromMentionId?: string, onlyRepliable?: boolean) => void
  expandPrevious: (fromMentionId?: string) => void
  collapse: () => void
  setResolution: (mentionId: string, isResolved: boolean) => void
  /** Every visible mention is resolved this session — drives the empty state. */
  allLocallyResolved: boolean
}

/**
 * Mentions adapter over the shared coordination driver. Owns the mention
 * candidacy rule (channel-level repliability + not already resolved on the
 * server) and delegates navigation/reset to `useEngagementListCoordination`.
 * Mentions advance immediately (no scroll); the newly expanded card's textarea
 * focus brings it into view.
 */
export const useMentionListCoordination = ({
  mentions,
  channelById,
  filters,
  isLoading = false,
}: UseMentionListCoordinationParams): UseMentionListCoordinationReturn => {
  const { config } = useCommunityConfig()
  const store = useEngagementCoordinationStoreApi()
  const locallyResolved = useEngagementCoordinationStore(selectLocallyResolved)
  const locallyUnresolved = useEngagementCoordinationStore(
    selectLocallyUnresolved,
  )

  // A mention is repliable when its channel and type allow replying in Buffer.
  // Coordination uses this to skip notify-only cards.
  const isMentionRepliable = React.useCallback(
    (mention: MentionData): boolean => {
      const channel = channelById.get(mention.channelId)
      if (!channel) return false
      return config.mentions.canReply(channel, mention.mentionType)
    },
    [channelById, config],
  )

  const coordinationItems = React.useMemo<CoordinationItem[]>(
    () =>
      mentions.map((mention) => ({
        id: mention.id,
        isRepliable:
          isMentionRepliable(mention) &&
          mention.status !== 'replied' &&
          mention.status !== 'dismissed',
      })),
    [mentions, isMentionRepliable],
  )

  // Channel ids in list order (Map preserves insertion order); the reset-key
  // helper sorts them for the expansion key and keeps list order for resolution.
  const channelIds = React.useMemo(() => [...channelById.keys()], [channelById])

  // Unanswered opens the first unanswered card; All (the default when no
  // status is set) opens the top card only if it's still unanswered.
  const isAllFilter = !filters?.status || filters.status === 'all'
  const shouldAutoExpand =
    filters?.status === 'unreplied' || filters?.status === 'new' || isAllFilter
  const { resetExpansionKey, resetResolutionKey } = buildCoordinationResetKeys(
    filters,
    channelIds,
  )

  useEngagementListCoordination({
    items: coordinationItems,
    shouldAutoExpand,
    autoExpandTopOnly: isAllFilter,
    isLoading,
    resetExpansionKey,
    resetResolutionKey,
    cardWrapperSelector: '[data-mention-wrapper="true"]',
  })

  // Whether every visible mention is resolved this session. Needs each
  // mention's server status, so it's derived here rather than in the store.
  const allLocallyResolved = React.useMemo(
    () =>
      deriveAllResolved(
        mentions.map((mention) => ({
          id: mention.id,
          isResolvedOnServer:
            mention.status === 'replied' || mention.status === 'dismissed',
        })),
        locallyResolved,
        locallyUnresolved,
      ),
    [mentions, locallyResolved, locallyUnresolved],
  )

  const { expand, expandNext, expandPrevious, collapse, setResolution } =
    store.getState()

  return {
    expand,
    expandNext,
    expandPrevious,
    collapse,
    setResolution,
    allLocallyResolved,
  }
}
